/**
 * CloseButton Component (X Button)
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 1171-527
 *
 * 24x24px close button with X icon (8pt grid compliant)
 * Six states: Default, Hover, Active, Disable, Warning, Outline
 */

import React, { useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Icon } from '../icons';
import { colors } from '../tokens/colors';
import { Colors } from '../../../themes/colors';

export interface CloseButtonProps {
  /**
   * Button state
   * @default "default"
   */
  state?: 'default' | 'hover' | 'active' | 'disable' | 'warning' | 'outline';

  /**
   * Callback when button is pressed
   */
  onPress?: () => void;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
}

export const CloseButton: React.FC<CloseButtonProps> = ({
  state = 'default',
  onPress,
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Determine current visual state
  const getCurrentState = () => {
    if (disabled || state === 'disable') return 'disable';
    if (state === 'warning') return 'warning';
    if (state === 'outline') return 'outline';
    if (isPressed || state === 'active') return 'active';
    if (isHovered || state === 'hover') return 'hover';
    return 'default';
  };

  const currentState = getCurrentState();

  // Get background color using Crop Circle color tokens
  const getBackgroundColor = () => {
    switch (currentState) {
      case 'active':
        return Colors.role.surfaceInverse;
      case 'hover':
        return Colors.role.surfaceInverseSoftHover; // #C8CDD5
      case 'warning':
      case 'outline':
        return colors.orange[500]; // #FF5505 - brand core
      case 'disable':
      case 'default':
      default:
        return Colors.role.surfaceInverseSoft; // #ECEEF1
    }
  };

  // Get icon color
  const getIconColor = () => {
    switch (currentState) {
      case 'active':
        return Colors.role.textOnInverse;
      case 'warning':
      case 'outline':
        return Colors.role.textOnBrand;
      case 'disable':
        return Colors.role.textDisabled; // #B4B4B4
      case 'hover':
      case 'default':
      default:
        return Colors.role.textStrong; // #020202 in light, white on the dark soft fill
    }
  };

  const backgroundColor = getBackgroundColor();
  const iconColor = getIconColor();

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onPressIn={() => !disabled && setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onHoverIn={() => !disabled && setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={disabled}
      style={[
        styles.container,
        {
          backgroundColor,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        },
      ]}
    >
      <Icon name="x" size={12} color={iconColor} />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 24,
    height: 24,
    borderRadius: 99, // Fully circular
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
});

CloseButton.displayName = 'CloseButton';
